import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useT } from '../utils/translate';
import { useModalOpen } from '../utils/modalLock';
import axios from 'axios';
import toast from 'react-hot-toast';
import { invalidateContacts } from '../store/contactsSlice';
import { getSocket } from '../utils/socket';
import { usePermissions } from '../utils/permissions';
import CallButton from '../components/CallButton';
import { PageHeader, SearchInput, FilterPopover, FilterField, FilterSelect, FilterChips, MoreMenu, Segmented } from '../components/ui/PageToolbar';
import {
  DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors,
  closestCorners, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, X, Loader2, Check, ChevronRight, User, UserCheck, Phone, DollarSign, Pencil, Trash2, Clock, Calendar, Download, Upload, Layers, ChevronDown, Archive, ArchiveRestore, Trophy, XCircle, RotateCcw } from 'lucide-react';
import { localeShortDateTime } from '../utils/date';
import { fmtNumber, fmtCompact, fmtPhone, currencyLabel } from '../utils/format';
import { srv } from '../utils/serverText';
import AssigneePicker, { AssigneeAvatars } from '../components/ui/AssigneePicker';
import { ownersOf, ownerIdsOf } from '../utils/assignees';
import Modal from '../components/ui/Modal';
import Celebrate from '../components/ui/Celebrate';
import { RequirementsModal, isRequirementsError, requirementsOf, flattenFields } from '../components/funnel/StageRules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const fmt = (n) => fmtNumber(n);


function fmtDate(d) {
  return localeShortDateTime(d);
}

function daysSince(d) {
  if (!d) return 0;
  return Math.floor((Date.now() - new Date(d)) / 86400000);
}

/* ── Deal card (draggable) ── */
function DealCard({ fadeDelay = 0, deal, isLead, onEdit, onDelete, onMove, onArchive, onClaim, onMoveStage, accent, currency, canEdit = true, canDelete = true, overlay = false, selectMode = false, selected = false, onToggleSelect }) {
  const t = useT();
  const paymentsOn = useSelector(st => st.auth.user?.organization?.settings?.dealPayments);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: deal._id, disabled: selectMode });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const days = daysSince(deal.createdAt);

  // Возраст сделки: до недели нейтрально, 8–14 дней предупреждение, дольше две недели просрочка (токены статусов)
  const ageTone = days > 14 ? 'bg-status-overdue-bg text-status-overdue' : days > 7 ? 'bg-status-progress-bg text-status-progress' : 'bg-status-hold-bg text-status-hold';
  const stop = e => { e.stopPropagation(); e.preventDefault(); };
  const iconBtn = 'p-1.5 rounded-md hover:bg-surface-100 text-ink-tertiary hover:text-ink transition-colors';

  // Действия: плавающая панель по наведению (не занимает места в карточке); на сенсорных экранах вместо неё кнопка «Этап» внизу
  const actions = (
    <div className="absolute top-1.5 right-1.5 z-[1] flex items-center gap-0.5 rounded-lg bg-card/95 border border-surface-200 shadow-card px-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity [@media(pointer:coarse)]:hidden">
      {canEdit && (
        <button onPointerDown={stop} onClick={e => { stop(e); onEdit(deal); }} className={iconBtn}><Pencil className="w-3.5 h-3.5" /></button>
      )}
      {canEdit && (
        <button onPointerDown={stop} onClick={e => { stop(e); onMove(deal); }} title={t('deals.moveToFunnel')} className={iconBtn}><Layers className="w-3.5 h-3.5" /></button>
      )}
      {canEdit && (
        <button onPointerDown={stop} onClick={e => { stop(e); onArchive(deal); }} title={t('loc.funnelPage.archive')} className={iconBtn}><Archive className="w-3.5 h-3.5" /></button>
      )}
      {canDelete && (
        <button onPointerDown={stop} onClick={e => { stop(e); onDelete(deal._id); }} className="p-1.5 rounded-md hover:bg-status-lost-bg text-ink-tertiary hover:text-status-lost transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
      )}
    </div>
  );

  const card = (
    <div style={accent ? { borderLeftColor: accent } : undefined}
      className={`relative bg-card rounded-xl border border-surface-200 border-l-[3px] shadow-card group select-none overflow-hidden ${overlay ? 'rotate-2 scale-[1.03] shadow-pop' : 'hover:shadow-card-hover hover:-translate-y-px transition-all duration-150 ease-soft'}`}>
      {actions}
      <div className="p-3">
        {isLead && (
          <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-status-progress-bg text-status-progress mb-1.5">{t('loc.funnelPage.request')}</span>
        )}
        <p className={`${isLead ? 'text-base' : 'text-sm'} font-semibold text-ink leading-snug break-words`}>{srv(deal.title)}</p>

        {/* Контакт: имя · телефон в одной строке, звонок в один тап */}
        {(deal.contact?.name || deal.contact?.phone) && (
          <div className="flex items-center gap-1.5 mt-1.5 min-w-0">
            <User className="w-3 h-3 text-ink-tertiary shrink-0" />
            <span className="text-xs text-ink-secondary truncate min-w-0">
              {deal.contact?.name ? srv(deal.contact.name) : ''}
              {deal.contact?.name && deal.contact?.phone ? ' · ' : ''}
              {deal.contact?.phone ? <span className="tnum">{fmtPhone(deal.contact.phone)}</span> : null}
            </span>
            {deal.contact?.phone && (
              <span className="ml-auto shrink-0" onPointerDown={stop}>
                <CallButton phone={deal.contact.phone} iconClassName="w-3.5 h-3.5"
                  className="p-1 rounded-lg text-status-won hover:bg-status-won-bg transition-colors shrink-0" />
              </span>
            )}
          </div>
        )}

        {/* Сумма и возраст */}
        {(deal.value > 0 || days > 1) && (
          <div className="flex items-center justify-between gap-2 mt-2">
            {deal.value > 0
              ? <span className="text-sm font-bold text-ink tnum">{fmt(deal.value)} <span className="text-xs font-medium text-ink-tertiary">{currency}</span></span>
              : <span />}
            {days > 1 && (
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-md ${ageTone}`} title={t('loc.funnelPage.age', { days })}>{t('loc.funnelPage.d', { days })}</span>
            )}
          </div>
        )}
        {paymentsOn && deal.debt > 0 && (
          <div className="mt-1.5">
            <span className="inline-flex items-center rounded-full bg-status-lost-bg px-2 py-0.5 text-xs font-semibold text-status-lost">
              {t('dealFinance.debt')}: {fmt(deal.debt)} {currency}
            </span>
          </div>
        )}

        {/* Низ: дата у лида; ответственный (или «Взять себе») у сделки */}
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-surface-100">
          {isLead ? (
            <div className="flex items-center gap-1.5 text-xs text-ink-tertiary min-w-0">
              <Calendar className="w-3 h-3 shrink-0" /><span className="truncate" title={fmtDate(deal.createdAt)}>{fmtDate(deal.createdAt)}</span>
            </div>
          ) : ownersOf(deal).length ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <AssigneeAvatars users={ownersOf(deal)} max={3} />
              <span className="text-xs text-ink-tertiary truncate">
                {ownersOf(deal)[0]?.name}{ownersOf(deal).length > 1 ? ` +${ownersOf(deal).length - 1}` : ''}
              </span>
            </div>
          ) : onClaim && canEdit ? (
            <button onPointerDown={stop} onClick={e => { stop(e); onClaim(deal._id); }}
              className="text-xs font-semibold px-2 py-1 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors">
              {t('loc.funnelPage.takeIt')}
            </button>
          ) : <div />}
          {onMoveStage && canEdit && (
            <button onPointerDown={stop} onClick={e => { stop(e); onMoveStage(deal); }}
              className="ml-auto hidden [@media(pointer:coarse)]:inline-flex items-center gap-1 min-h-[36px] px-2.5 rounded-lg text-xs font-semibold bg-surface-100 text-ink-secondary">
              <ChevronRight className="w-3.5 h-3.5" />{t('deals.moveStage')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (overlay) return card;
  return (
    <div ref={setNodeRef} style={{ ...style, animationDelay: fadeDelay ? `${fadeDelay}ms` : undefined }} className="relative animate-fade-in">
      {selectMode && (
        <button
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); e.preventDefault(); onToggleSelect(deal._id); }}
          className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
            selected ? 'bg-primary-600 border-primary-600' : 'bg-white border-surface-300'
          }`}
        >
          {selected && <Check className="w-3.5 h-3.5 text-white" />}
        </button>
      )}
      <div {...(selectMode ? {} : { ...attributes, ...listeners, role: "group" })}
        className={selectMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}
        onClick={e => {
          if (selectMode) { onToggleSelect(deal._id); return; }
          // Only navigate if it wasn't a real drag (distance constraint handles this, but
          // we additionally skip if the user clicked on action buttons)
          if (!isDragging && !e.defaultPrevented) onEdit(deal);
        }}>
        <div className={selectMode && selected ? 'ring-2 ring-primary-400 rounded-xl' : ''}>{card}</div>
      </div>
    </div>
  );
}

/* ── Yopilgan sdelka kartochkasi ("G'olib"/"Yo'qotilgan" ustunlarida) — drag yo'q,
   o'rniga "Qayta faollashtirish" tugmasi bilan istalgan voronka/bosqichga qaytariladi ── */
function ClosedDealCard({ deal, currency, onOpen, onReactivate, canEdit = true }) {
  const t = useT();
  const paymentsOn = useSelector(st => st.auth.user?.organization?.settings?.dealPayments);
  return (
    <div className="bg-white rounded-xl border border-surface-200 shadow-card overflow-hidden">
      <div className={`h-1 w-full ${deal.status === 'won' ? 'bg-emerald-400' : 'bg-red-400'}`} />
      <div className="p-3 cursor-pointer" onClick={() => onOpen(deal)}>
        <p className="text-sm font-bold text-ink leading-tight mb-1.5">{srv(deal.title)}</p>
        {deal.contact?.phone && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <Phone className="w-3 h-3 text-ink-tertiary shrink-0" />
            <span className="text-xs text-ink-secondary font-medium tabular-nums">{fmtPhone(deal.contact.phone)}</span>
          </div>
        )}
        {deal.value > 0 && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <DollarSign className="w-3 h-3 text-primary-500 shrink-0" />
            <span className="text-xs font-bold text-primary-700">{fmt(deal.value)} {currency}</span>
          </div>
        )}
        {/* Qarz — to'lovlar yuritiladigan tashkilotlarda */}
        {paymentsOn && deal.debt > 0 && (
          <div className="mb-1.5">
            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
              {t('dealFinance.debt')}: {fmt(deal.debt)} {currency}
            </span>
          </div>
        )}
        {deal.closeReason && (
          <p className="text-xs text-ink-tertiary bg-surface-50 rounded-lg px-2 py-1 mt-1.5 line-clamp-2">{deal.closeReason}</p>
        )}
      </div>
      {canEdit && (
        <button
          onClick={e => { e.stopPropagation(); onReactivate(deal); }}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-600 border-t border-surface-100 hover:bg-primary-50 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" /> {' '}{t('loc.funnelPage.reactivate')}
        </button>
      )}
    </div>
  );
}

/* ── "G'olib" / "Yo'qotilgan" ustuni (drag'siz, statik) ── */
function ClosedColumn({ label, color, icon: Icon, deals, currency, onOpen, onReactivate, canEdit }) {
  const t = useT();
  const total = deals.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div className="flex flex-col w-80 shrink-0 h-full">
      <div className="text-center mb-3 px-2">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
          <span className="font-bold text-sm text-ink truncate">{label}</span>
        </div>
        <span className="text-xs text-ink-tertiary">
          {t('loc.funnelPage.dealsLabel')}{' '}<span className="font-semibold" style={{ color }}>{deals.length}</span>
          {total > 0 && <span className="ml-1">• <span className="font-semibold">{fmtCompact(total, t)} {currency}</span></span>}
        </span>
      </div>
      <div className="flex-1 rounded-xl p-2 space-y-2 overflow-y-auto bg-surface-100">
        {deals.length === 0 ? (
          <p className="text-xs text-ink-tertiary text-center py-6">{t('loc.funnelPage.empty')}</p>
        ) : (
          deals.map(deal => (
            <ClosedDealCard key={deal._id} deal={deal} currency={currency} onOpen={onOpen} onReactivate={onReactivate} canEdit={canEdit} />
          ))
        )}
      </div>
    </div>
  );
}

/* ── Stage column (droppable) ── */
function StageColumn({ stage, highlight = false, deals, onOpen, onDelete, onMove, onArchive, onClaim, onMoveStage, onQuickAdd, currency, isFirst, canCreate = true, canEdit = true, canDelete = true, selectMode = false, selectedIds, onToggleSelect, onSelectAllStage }) {
  const t = useT();
  const { setNodeRef, isOver } = useDroppable({ id: stage._id });
  const total = deals.reduce((s, d) => s + (d.value || 0), 0);
  const stageAllSelected = selectMode && deals.length > 0 && deals.every(d => selectedIds?.has(d._id));
  // Отрисовываем первые 40 карточек, остальные по кнопке: воронка на 1000+ сделок не тормозит
  const [limit, setLimit] = useState(40);
  const shown = deals.length > limit ? deals.slice(0, limit) : deals;

  return (
    <div data-stage={stage._id} className="flex flex-col w-[86vw] max-w-sm sm:w-80 shrink-0 h-full snap-center sm:snap-align-none">
      {/* Заголовок этапа: цвет, название, счётчик и сумма */}
      <div className="px-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {selectMode && deals.length > 0 && (
            <input type="checkbox" checked={stageAllSelected} onChange={() => onSelectAllStage(stage._id)}
              title={t('loc.funnelPage.selectEverythingInThisStage')}
              className="w-3.5 h-3.5 rounded border-surface-300 shrink-0" />
          )}
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: stage.color }} />
          <span className="font-semibold text-sm text-ink truncate">{stage.name}</span>
          <span className="text-xs font-semibold bg-surface-100 text-ink-secondary rounded-full px-2 py-0.5 tnum shrink-0">{deals.length}</span>
          {canCreate && (
            <button
              onClick={() => onQuickAdd(stage._id)}
              className="ml-auto p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary hover:text-primary-700 transition-colors"
              title={t('loc.funnelPage.quickAdd')}
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-ink-tertiary mt-0.5 pl-[18px]">
          {String(isFirst ? t('loc.funnelPage.request2') : t('loc.funnelPage.dealsLabel')).replace(/[:\s]+$/, '')}
          {total > 0 && <span> · <span className="font-semibold text-ink-secondary tnum">{fmtCompact(total, t)} {currency}</span></span>}
        </p>
      </div>

      {/* Cards — scrollable, fills remaining height */}
      <div
        ref={setNodeRef}
        className={`flex-1 rounded-2xl p-2 space-y-2 overflow-y-auto transition-colors duration-150 ${(isOver || highlight) ? 'bg-primary-500/10 ring-2 ring-primary-500/50' : 'bg-surface-100'}`}
      >
        <SortableContext items={shown.map(d => d._id)} strategy={verticalListSortingStrategy}>
          {shown.map((deal, i) => (
            <DealCard key={deal._id} fadeDelay={i < 12 ? i * 25 : 0} deal={deal} isLead={isFirst} accent={stage.color} currency={currency} onEdit={() => onOpen(deal._id)} onDelete={onDelete} onMove={onMove} onArchive={onArchive} onClaim={onClaim} onMoveStage={onMoveStage}
              canEdit={canEdit} canDelete={canDelete}
              selectMode={selectMode} selected={selectedIds?.has(deal._id)} onToggleSelect={onToggleSelect} />
          ))}
        </SortableContext>
        {deals.length === 0 && (
          <div className={`border-2 border-dashed rounded-xl px-3 py-8 text-center text-xs transition-colors ${isOver ? 'border-primary-500/50 text-primary-700' : 'border-surface-200 text-ink-tertiary'}`}>
            {t('loc.funnelPage.empty')}
          </div>
        )}
        {deals.length > shown.length && (
          <button type="button" onClick={() => setLimit(l => l + 40)}
            className="w-full py-2 rounded-xl text-xs font-semibold text-primary-700 bg-card border border-surface-200 hover:bg-surface-50">
            {t('deals.showMore', { count: deals.length - shown.length })}
          </button>
        )}
      </div>

    </div>
  );
}

/* ── Этапы на телефоне: чипы над доской (тап листает к колонке, активная подсвечивается при свайпе) ── */
function StageChips({ stages, dealsByStage, boardRef }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const mid = el.scrollLeft + el.clientWidth / 2;
        let best = 0, bestD = Infinity;
        el.querySelectorAll('[data-stage]').forEach((c, i) => {
          const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
          if (d < bestD) { bestD = d; best = i; }
        });
        setActive(best);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { cancelAnimationFrame(raf); el.removeEventListener('scroll', onScroll); };
  }, [boardRef, stages.length]);
  const go = (id) => boardRef.current?.querySelector(`[data-stage="${id}"]`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  return (
    <div className="sm:hidden flex gap-1.5 overflow-x-auto no-scrollbar px-3 py-2 shrink-0" role="tablist">
      {stages.map((st, i) => (
        <button key={st._id} type="button" role="tab" aria-selected={active === i} onClick={() => go(st._id)}
          className={`shrink-0 flex items-center gap-1.5 min-h-[36px] px-3 rounded-full text-xs font-semibold border transition-colors ${active === i ? 'bg-primary-500/15 text-primary-700 border-primary-500/40' : 'bg-card text-ink-secondary border-surface-200'}`}>
          <span className="w-2 h-2 rounded-full" style={{ background: st.color }} />{st.name}
          <span className="tnum text-ink-tertiary">{(dealsByStage[st._id] || []).length}</span>
        </button>
      ))}
    </div>
  );
}

/* ── Deal form modal ── */
function DealModal({ stageId, stages, contacts, users, deal, isLead, currency, onSave, onClose, onContactCreated }) {
  const t = useT();
  useModalOpen();
  const [title,      setTitle]      = useState(deal?.title || '');
  const [stage,      setStage]      = useState(stageId || deal?.stageId || stages[0]?._id || '');
  const [value,      setValue]      = useState(deal?.value ?? '');
  const [source,     setSource]     = useState(deal?.source || '');
  const [notes,      setNotes]      = useState(deal?.notes || '');
  const [assignees, setAssignees] = useState(ownerIdsOf(deal));
  const [contact,    setContact]    = useState(deal?.contact?._id || deal?.contact || '');
  const [saving,     setSaving]     = useState(false);
  const [cSearch,    setCSearch]    = useState('');
  const [dealSources, setDealSources] = useState([]);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact,     setNewContact]     = useState({ name: '', phone: '', email: '' });
  const [savingContact,  setSavingContact]  = useState(false);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
    axios.get(`${API_URL}/organization/deal-sources`)
      .then(r => setDealSources(r.data.sources || []))
      .catch(() => {});
  }, []);

  const filteredC = contacts.filter(c =>
    !cSearch || c.name.toLowerCase().includes(cSearch.toLowerCase()) || c.phone?.includes(cSearch)
  ).slice(0, 30);

  const submit = async () => {
    if (!title.trim()) { toast.error(t('funnel.titleRequired')); return; }
    setSaving(true);
    try {
      await onSave({ title: title.trim(), stageId: stage, value: Number(value) || 0, source: source || '', notes, assignees, contact: contact || null });
      onClose();
    } finally { setSaving(false); }
  };

  const handleCreateContact = async () => {
    if (!newContact.name.trim() || savingContact) return;
    setSavingContact(true);
    try {
      const res = await axios.post(`${API}/contacts`, { ...newContact, assignees: assignees.length ? assignees : undefined });
      onContactCreated?.(res.data.contact);
      setContact(res.data.contact._id);
      toast.success(t('funnel.contactCreated'));
      setShowNewContact(false);
      setNewContact({ name: '', phone: '', email: '' });
      setCSearch('');
    } catch (e) {
      const duplicate = e.response?.data?.duplicate;
      if (duplicate) {
        onContactCreated?.(duplicate);
        setContact(duplicate._id);
        toast.success(`${t('funnel.contactExists')}: ${duplicate.name}`);
        setShowNewContact(false);
        setNewContact({ name: '', phone: '', email: '' });
        setCSearch('');
      } else {
        toast.error(e.response?.data?.message || t('funnel.loadError'));
      }
    } finally {
      setSavingContact(false);
    }
  };

  const isLeadStage = stages.length > 0 && String(stage) === String(stages[0]?._id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-md flex flex-col max-h-[90dvh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100 shrink-0">
          <h2 className="font-semibold text-ink">
            {deal ? t('funnel.edit') : (isLeadStage ? t('funnel.newLead') : t('funnel.newDeal'))}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-ink mb-1">
              {isLeadStage ? t('funnel.leadNameLabel') : t('deals.titleLabel')}
            </label>
            <input className="input" placeholder={isLeadStage ? t('funnel.leadNameLabel') : t('deals.titlePlaceholder')} value={title} onChange={e => setTitle(e.target.value)} autoFocus />
          </div>
          {/* Stage */}
          <div>
            <label className="block text-xs font-medium text-ink mb-1">{t('deals.stage')}</label>
            <select className="input" value={stage} onChange={e => setStage(e.target.value)}>
              {stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          {/* Value */}
          <div>
            <label className="block text-xs font-medium text-ink mb-1">{t('deals.value')} ({currency})</label>
            <input className="input" type="number" min="0" placeholder="0" value={value} onChange={e => setValue(e.target.value)} />
          </div>
          {/* Source */}
          {dealSources.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-ink mb-1">{t('loc.funnelPage.source')}</label>
              <select className="input" value={source} onChange={e => setSource(e.target.value)}>
                <option value="">{t('loc.funnelPage.notSelected')}</option>
                {dealSources.map(s => (
                  <option key={s._id} value={String(s._id)}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Contact */}
          <div>
            <label className="block text-xs font-medium text-ink mb-1">{t('funnel.contactOpt')}</label>
            <input className="input mb-1.5 text-sm" placeholder={t('funnel.searchPlaceholder')} value={cSearch} onChange={e => setCSearch(e.target.value)} />
            <div className="border border-surface-200 rounded-lg max-h-32 overflow-y-auto">
              <button type="button" onClick={() => setContact('')}
                className={`w-full text-left px-3 py-2 text-xs transition-colors ${!contact ? 'bg-primary-50 text-primary-700 font-medium' : 'text-ink-tertiary hover:bg-surface-50'}`}>
                {t('funnel.noContact')}
              </button>
              {filteredC.map(c => (
                <button key={c._id} type="button" onClick={() => setContact(c._id)}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors border-t border-surface-100 ${contact === c._id ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-surface-50 text-ink'}`}>
                  <span className="font-medium">{srv(c.name)}</span>
                  {c.phone && <span className="text-ink-tertiary ml-2">{c.phone}</span>}
                </button>
              ))}
            </div>

            {showNewContact ? (
              <div className="mt-2 bg-surface-50 border border-surface-200 rounded-xl p-3 space-y-2">
                <input className="input text-xs" placeholder={t('funnel.contactNamePh')}
                  value={newContact.name} onChange={e => setNewContact(f => ({ ...f, name: e.target.value }))} autoFocus />
                <input className="input text-xs" placeholder={t('funnel.contactPhonePh')}
                  value={newContact.phone} onChange={e => setNewContact(f => ({ ...f, phone: e.target.value }))} />
                <input className="input text-xs" placeholder={t('funnel.contactEmailPh')}
                  value={newContact.email} onChange={e => setNewContact(f => ({ ...f, email: e.target.value }))} />
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => setShowNewContact(false)}
                    className="flex-1 py-1.5 text-xs rounded-lg bg-surface-100 text-ink-secondary hover:bg-surface-200 transition-colors">
                    {t('deals.cancel')}
                  </button>
                  <button type="button" onClick={handleCreateContact} disabled={!newContact.name.trim() || savingContact}
                    className="flex-1 py-1.5 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-1">
                    {savingContact ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    {t('deals.save')}
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => { setNewContact(f => ({ ...f, name: f.name || title })); setShowNewContact(true); }}
                className="mt-1.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-surface-300 text-xs text-ink-tertiary hover:border-primary-400 hover:text-primary-600 transition-colors">
                <Plus className="w-3.5 h-3.5" />
                {t('funnel.createContact')}
              </button>
            )}
          </div>
          {/* Assigned to */}
          <div>
            <label className="block text-xs font-medium text-ink mb-1">{t('funnel.responsibleLabel')}</label>
            <AssigneePicker value={assignees} onChange={setAssignees} users={users} placeholder={t('funnel.unassigned')} />
          </div>
          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-ink mb-1">{t('deals.notes')}</label>
            <textarea className="input resize-none text-sm" rows={2} placeholder={t('funnel.notesPlaceholder')} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-surface-100 shrink-0">
          <button onClick={onClose} className="btn-secondary btn-md">{t('deals.cancel')}</button>
          <button onClick={submit} disabled={saving} className="btn-primary btn-md flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {deal ? t('deals.save') : t('funnel.addBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main FunnelPage ── */
/* ── Import leads modal (.xlsx) ── */
function ImportLeadsModal({ funnelId, funnelName, onClose, onDone }) {
  const t = useT();
  const [file,      setFile]      = useState(null);
  const [importing, setImporting] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);
  const [result,    setResult]    = useState(null);
  const fileRef = useRef(null);

  const handleTemplate = async () => {
    setTplLoading(true);
    try {
      const res = await axios.get(`${API}/funnels/${funnelId}/deals/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leadlar-shablon.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('funnel.templateError'));
    } finally {
      setTplLoading(false);
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) { toast.error(t('funnel.onlyXlsx')); return; }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    pickFile(e.dataTransfer.files?.[0]);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/import`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || t('funnel.importError'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-lg flex flex-col max-h-[85dvh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100 shrink-0">
          <h2 className="font-semibold text-ink flex items-center gap-2">
            <Upload className="w-4 h-4 text-primary-600" /> {t('funnel.importTitle')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>

        {result ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-emerald-700" />
            </div>
            <p className="text-lg font-bold text-ink mb-1">{t('funnel.importDone')}</p>
            <p className="text-sm text-ink-secondary">
              <span className="text-emerald-700 font-semibold">{result.created}</span> {t('funnel.importCreated')},{' '}
              <span className="text-ink-tertiary">{result.skipped}</span> {t('funnel.importSkipped')}
            </p>
            <button onClick={onClose} className="btn-primary btn-md mt-6">{t('funnel.close')}</button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                <p className="font-semibold mb-1">{t('funnel.importFormat')}</p>
                <code className="font-mono">{t('loc.funnelPage.titleStageContactPhoneSource')}</code>
                <p className="mt-1 text-amber-700">{t('funnel.importHint')}</p>
                <button onClick={handleTemplate} disabled={tplLoading}
                  className="mt-2 inline-flex items-center gap-1.5 font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900 disabled:opacity-50">
                  {tplLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {t('funnel.downloadTemplate')}
                </button>
              </div>

              {!file ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-surface-300 rounded-2xl p-10 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors"
                >
                  <Upload className="w-8 h-8 text-ink-disabled mx-auto mb-2" />
                  <p className="text-sm font-medium text-ink-secondary">{t('funnel.dropXlsx')}</p>
                  <p className="text-xs text-ink-tertiary mt-1">{t('funnel.orClick')}</p>
                  <input ref={fileRef} type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden" onChange={e => pickFile(e.target.files?.[0])} />
                </div>
              ) : (
                <div className="flex items-center justify-between border border-surface-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-medium text-ink truncate">{file.name}</p>
                  <button onClick={() => setFile(null)} className="text-xs text-ink-tertiary hover:text-red-600 flex items-center gap-1 shrink-0">
                    <X className="w-3 h-3" /> {t('funnel.clear')}
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t border-surface-100 shrink-0">
              <button onClick={onClose} className="btn-secondary btn-md">{t('funnel.cancel')}</button>
              <button onClick={handleImport} disabled={!file || importing}
                className="btn-primary btn-md flex items-center gap-2">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {t('funnel.importBtn')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Arxivlangan lidlar ── */
function DealArchiveModal({ funnelId, stages, onClose, onRestored, canEdit = true, canDelete = true }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  useModalOpen();
  const [deals,   setDeals]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId,  setBusyId]  = useState(null);

  useEffect(() => {
    axios.get(`${API}/funnels/${funnelId}/deals`, { params: { archived: true } })
      .then(r => setDeals(r.data.deals || []))
      .catch(() => toast.error(t('loc.funnelPage.failedToLoad')))
      .finally(() => setLoading(false));
  }, [funnelId]);

  const stageName = (id) => stages.find(s => String(s._id) === String(id))?.name || '—';

  const restore = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/funnels/${funnelId}/deals/bulk-archive`, { dealIds: [id], archived: false });
      setDeals(prev => prev.filter(x => x._id !== id));
      onRestored();
      toast.success(t('loc.funnelPage.restoredFromArchive'));
    } catch (e) { toast.error(e.response?.data?.message || t('loc.funnelPage.error')); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/funnels/${funnelId}/deals`, { data: { dealIds: [id] } });
      setDeals(prev => prev.filter(x => x._id !== id));
      toast.success(t('loc.funnelPage.deleted'));
    } catch (e) { toast.error(e.response?.data?.message || t('loc.funnelPage.error')); }
    finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100 shrink-0">
          <h2 className="font-semibold text-ink flex items-center gap-2">
            <Archive className="w-4 h-4" /> {' '}{t('loc.funnelPage.archive2')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary-400" />
            </div>
          ) : deals.length === 0 ? (
            <p className="text-sm text-ink-tertiary text-center py-10">{t('loc.funnelPage.theArchiveIsEmpty')}</p>
          ) : (
            <div className="space-y-2">
              {deals.map(deal => (
                <div key={deal._id} className="flex items-start gap-3 p-3 border border-surface-100 rounded-xl">
                  <button
                    onClick={() => {
                      onClose();
                      // Hozirgi (/funnel/X) yozuvga "openArchive" belgisini qo'yib qo'yamiz —
                      // shunda sdelka ichidan brauzer tarixi orqasiga (navigate(-1)) qaytilganda
                      // filtrlar (URL query) va Arxiv oynasi ikkalasi ham avtomatik tiklanadi.
                      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: { openArchive: true } });
                      navigate(`/funnel/${funnelId}/deal/${deal._id}`);
                    }}
                    className="flex-1 min-w-0 text-left hover:bg-surface-50 -m-1 p-1 rounded-lg transition-colors"
                    title={t('loc.funnelPage.openDeal')}
                  >
                    <p className="text-sm font-medium text-ink truncate">{srv(deal.title)}</p>
                    <p className="text-xs text-ink-tertiary truncate">{stageName(deal.stageId)}</p>
                    {deal.contact?.phone && (
                      <p className="text-xs text-ink-secondary truncate mt-0.5">
                        {deal.contact.name ? `${srv(deal.contact.name)} — ` : ''}{fmtPhone(deal.contact.phone)}
                      </p>
                    )}
                    {deal.archiveReason && (
                      <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1 mt-1.5">
                        {deal.archiveReason}
                      </p>
                    )}
                  </button>
                  {canEdit && (
                    <button onClick={() => restore(deal._id)} disabled={busyId === deal._id}
                      title={t('loc.funnelPage.restore')}
                      className="p-1.5 rounded-lg text-ink-tertiary hover:bg-primary-50 hover:text-primary-600 transition-colors">
                      <ArchiveRestore className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(deal._id)} disabled={busyId === deal._id}
                      className="p-1.5 rounded-lg text-ink-tertiary hover:bg-red-50 hover:text-red-600 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FunnelPage({ funnelId }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();
  const t = useT();
  const currency  = currencyLabel(useSelector(s => s.auth.user?.organization?.currency || 'UZS'), t);
  const perm = usePermissions();
  const canCreate = perm.can('funnels', 'create');
  const canEdit   = perm.can('funnels', 'edit');
  const canDelete = perm.can('funnels', 'delete');
  const [funnel,      setFunnel]      = useState(null);
  const [deals,       setDeals]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeId,    setActiveId]    = useState(null);
  // Filtrlar sahifa URL'idagi query'dan boshlang'ich qiymat oladi — shunda lid
  // ichiga kirib "orqaga" qaytilganda (brauzer tarixi o'sha URL'ga qaytadi) filtrlar
  // yo'qolib qolmaydi, chunki komponent qayta yaratilganda ham o'sha yerdan o'qiydi.
  const initFilters = new URLSearchParams(location.search);
  const [search,      setSearch]      = useState(initFilters.get('q') || '');
  const [filterAssignedTo, setFilterAssignedTo] = useState(initFilters.get('assignee') || ''); // '' = hammasi
  const [sortBy, setSortBy] = useState(initFilters.get('sort') || ''); // '' = standart (surish tartibi)
  const [filterSource, setFilterSource] = useState(initFilters.get('source') || '');         // '' = hammasi, '__none__' = manbasiz
  const [filterCF,     setFilterCF]     = useState(() => {          // { [fieldId]: value } — dropdown/multiselect custom maydonlar
    const obj = {};
    for (const [k, v] of initFilters.entries()) if (k.startsWith('cf_')) obj[k.slice(3)] = v;
    return obj;
  });
  const [filterDateFrom, setFilterDateFrom] = useState(initFilters.get('from') || ''); // yaratilgan sana bo'yicha filtr — dan
  const [filterDateTo,   setFilterDateTo]   = useState(initFilters.get('to') || ''); // yaratilgan sana bo'yicha filtr — gacha
  // Yopilgan (g'olib/yo'qotilgan) sdelkalar odatiy holatda taxtadan yashiringan —
  // amoCRM'dagi kabi, faqat shu belgi yoqilsa ko'rinadi.
  const [showClosed, setShowClosed] = useState(initFilters.get('closed') === '1');
  // Yopilgan sdelkani qayta faollashtirish — istalgan voronka/bosqichga qaytarish
  const [reactivateDeal,     setReactivateDeal]     = useState(null);
  const [reactivateFunnelId, setReactivateFunnelId] = useState('');
  const [reactivateStageId,  setReactivateStageId]  = useState('');
  const [reactivating,       setReactivating]       = useState(false);
  const [dealSources,  setDealSources]  = useState([]);
  const [cfSections,   setCfSections]   = useState([]);
  const [pendingMove, setPendingMove] = useState(null);
  const [moveValue,   setMoveValue]   = useState('');
  // Bosqich "mas'ul tanlash majburiy" deb belgilangan bo'lsa (masalan qabuldan
  // menejerga topshirish) - sudrab tashlaganda mas'ul so'raladi, tasdiqlangach
  // applyMove shu odam bilan chaqiriladi (backend handoffTo bo'lsa avtomatik
  // boshqa voronkaga ham o'tkazadi).
  const [pendingAssign, setPendingAssign] = useState(null); // { deal, targetStageId }
  const [assignUserId,  setAssignUserId]  = useState('');
  // Sudrab "G'olib"/"Yo'qotilgan" deb belgilangan bosqichga tashlaganda — menyudagi
  // umumiy yopish bilan bir xil, sabab so'raladi (drag orqali ham "jim-jim" yopilmasin)
  const [closeReasons,     setCloseReasons]     = useState({ won: [], lost: [] });
  const [dragCloseModal,   setDragCloseModal]   = useState(null); // { deal, targetStageId, targetStatus }
  const [celebrate,         setCelebrate]         = useState(false);
  // Bosqich majburiy maydonlari to'ldirilmagan: { deal, missing, retry(values, value) }
  const [reqModal, setReqModal] = useState(null);
  const [reqBusy,  setReqBusy]  = useState(false);
  const [dragCloseReasonInput, setDragCloseReasonInput] = useState('');
  const [dragClosing,      setDragClosing]      = useState(false);

  // Boshqa varonkaga o'tkazish (kartochkadagi tugma)
  const [moveDeal,          setMoveDeal]          = useState(null); // qaysi deal o'tkazilyapti
  const [moveFunnelId,      setMoveFunnelId]      = useState('');
  const [moveStageId,       setMoveStageId]       = useState('');
  const [moveAssignUserId,  setMoveAssignUserId]  = useState('');
  const [movingFunnel,      setMovingFunnel]      = useState(false);

  // Ommaviy (bulk) amallar: bir nechta lidni tanlab bosqich/varonka o'zgartirish
  const [selectMode,        setSelectMode]        = useState(false);
  const [selectedIds,       setSelectedIds]       = useState(() => new Set());
  const [bulkMovingStage,   setBulkMovingStage]   = useState(false);
  const [bulkMoveOpen,      setBulkMoveOpen]      = useState(false);
  const [bulkMoveFunnelId,  setBulkMoveFunnelId]  = useState('');
  const [bulkMoveStageId,   setBulkMoveStageId]   = useState('');
  const [bulkMovingFunnel,  setBulkMovingFunnel]  = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting,      setBulkDeleting]      = useState(false);
  const [bulkArchiving,     setBulkArchiving]     = useState(false);
  const [archiveTargetIds,  setArchiveTargetIds]  = useState(null); // null = yopiq; array = ochiq (1 yoki ko'p lid)
  const [bulkArchiveReason, setBulkArchiveReason] = useState('');
  const [bulkAssigning,     setBulkAssigning]     = useState(false);
  const [showArchive,       setShowArchive]       = useState(false);

  // F-13: quick-add modal
  const [contacts,      setContacts]      = useState([]);
  const [users,         setUsers]         = useState([]);
  const [quickStageId,  setQuickStageId]  = useState(null); // null = closed
  const [showImport,    setShowImport]    = useState(false);
  const [exporting,     setExporting]     = useState(false);
  // Filtrlar paneli odatda yopiq; lekin filtr qo'llangan bo'lsa (masalan URL'dan) ochiq
  // boshlanadi — xodim taxtada nega kam lid borligini darhol tushunsin.
  // Barcha voronka nomlari (ko'rinish cheklovisiz) - "boshqa voronkaga yuborish" tanlovi uchun
  const [allFunnelNames, setAllFunnelNames] = useState([]);

  // Bosqich ustunlari ekranga sig'sa markazga tortiladi; sig'masa (ko'p bosqich)
  // odatdagidek chapdan boshlab scroll qilinadi.
  const boardRef = useRef(null);
  const [stageSheetDeal, setStageSheetDeal] = useState(null);   // телефон: сделка, для которой открыт выбор этапа
  const [boardFits, setBoardFits] = useState(false);
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const check = () => setBoardFits(el.scrollWidth <= el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [funnel, deals]);

  const sensors = useSensors(
    // Desktop: sichqoncha bilan 5px surilsa drag boshlanadi
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // Telefon: bosib turib (200ms) drag; tez swipe — ro'yxat scroll bo'ladi
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );

  const load = useCallback(async () => {
    if (!funnelId) return;
    setLoading(true);
    try {
      const [fRes, cRes, uRes, nRes, sRes, dfRes, crRes] = await Promise.all([
        axios.get(`${API}/funnels/${funnelId}/deals`),
        // Contacts moduli alohida RBAC bilan boshqariladi (masalan "operator" kabi
        // rol funnels'ga ega bo'lib, contacts'ga ega bo'lmasligi mumkin) — shu sabab
        // 403 bo'lsa ham butun boardni yuklashni to'xtatmaymiz, kontaktlar bo'sh qoladi.
        axios.get(`${API}/contacts?limit=200`).catch(() => ({ data: {} })),
        axios.get(`${API}/organization/users`),
        axios.get(`${API}/funnels/names`),
        axios.get(`${API}/organization/deal-sources`).catch(() => ({ data: {} })),
        axios.get(`${API}/organization/deal-fields`).catch(() => ({ data: {} })),
        axios.get(`${API}/organization/close-reasons`).catch(() => ({ data: {} })),
      ]);
      setFunnel(fRes.data.funnel);
      setDeals(fRes.data.deals);
      setContacts(cRes.data.contacts || []);
      setUsers(uRes.data.users || []);
      setAllFunnelNames(nRes.data.funnels || []);
      setDealSources(sRes.data.sources || []);
      setCfSections(dfRes.data.sections || []);
      setCloseReasons(crRes.data.closeReasons || { won: [], lost: [] });
    } catch (e) {
      console.error('[funnel] load error:', e.response?.data?.message || e.message);
      toast.error(t('funnel.loadError'));
    } finally {
      setLoading(false);
    }
  }, [funnelId]);

  useEffect(() => { load(); }, [load]);

  // Arxivdagi lidni ochib, "orqaga" bosilganda — arxiv oynasi qayta ochilsin
  useEffect(() => {
    if (location.state?.openArchive) {
      setShowArchive(true);
      // location.search (filtrlar) saqlanib qolishi kerak — faqat state tozalanadi
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Filtrlar o'zgarganda URL query'ga yozib boramiz — shunda lid ichiga kirib
  // "orqaga" qaytilganda (brauzer tarixi shu URL'ga qaytadi) filtrlar tiklanadi.
  useEffect(() => {
    const params = new URLSearchParams();
    if (search)              params.set('q', search);
    if (filterAssignedTo)    params.set('assignee', filterAssignedTo);
    if (filterSource)        params.set('source', filterSource);
    if (sortBy)               params.set('sort', sortBy);
    if (filterDateFrom)      params.set('from', filterDateFrom);
    if (filterDateTo)        params.set('to', filterDateTo);
    if (showClosed)           params.set('closed', '1');
    Object.entries(filterCF).forEach(([fid, val]) => { if (val) params.set(`cf_${fid}`, val); });
    const qs = params.toString();
    if (qs === location.search.replace(/^\?/, '')) return; // o'zgarish yo'q — ortiqcha navigate qilmaymiz
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterAssignedTo, filterSource, sortBy, filterDateFrom, filterDateTo, showClosed, filterCF]);

  /* Excel eksport */
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/funnels/${funnelId}/deals/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(funnel?.name || 'leadlar').replace(/[^\wЀ-ӿ\- ]+/g, '').trim() || 'leadlar'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('funnel.exportDone'));
    } catch {
      toast.error(t('funnel.exportError'));
    } finally {
      setExporting(false);
    }
  };

  // Real-time sync: boshqa foydalanuvchi lid qo'shsa/o'zgartirsa/o'chirsa yoki
  // varonka bosqichlarini tahrirlasa — sahifa avtomatik yangilanadi (refresh shart emas).
  useEffect(() => {
    if (!funnelId) return;
    const socket = getSocket();
    const sameFunnel = (fid) => String(fid) === String(funnelId);
    const onDealCreated = ({ funnelId: fid, deal }) => {
      if (!sameFunnel(fid)) return;
      setDeals(prev => prev.some(d => d._id === deal._id) ? prev : [...prev, deal]);
    };
    const onDealUpdated = ({ funnelId: fid, deal }) => {
      if (!sameFunnel(fid)) return;
      setDeals(prev => prev.map(d => d._id === deal._id ? deal : d));
    };
    const onDealDeleted = ({ funnelId: fid, dealId }) => {
      if (!sameFunnel(fid)) return;
      setDeals(prev => prev.filter(d => d._id !== dealId));
    };
    const onFunnelUpdated = ({ funnel: f }) => {
      if (!sameFunnel(f._id)) return;
      setFunnel(f);
    };
    socket.on('deal:created',   onDealCreated);
    socket.on('deal:updated',   onDealUpdated);
    socket.on('deal:deleted',   onDealDeleted);
    socket.on('funnel:updated', onFunnelUpdated);
    return () => {
      socket.off('deal:created',   onDealCreated);
      socket.off('deal:updated',   onDealUpdated);
      socket.off('deal:deleted',   onDealDeleted);
      socket.off('funnel:updated', onFunnelUpdated);
    };
  }, [funnelId]);

  /* Filtrlash mumkin bo'lgan custom maydonlar: faqat tanlovli (dropdown/
     multiselect) — teg, biznes yo'nalishi, hudud kabi. Matn/son maydonlar
     (Kompaniya, Xodimlar soni, Izoh) filtrga chiqmaydi. Xavfsizlik uchun
     ≤30 xil qiymat sharti ham qoladi. */
  const filterableFields = cfSections
    .flatMap(s => (s.fields || []))
    .filter(f => f && f.id && (f.type === 'dropdown' || f.type === 'multiselect'))
    .map(f => {
      const fromOptions = Array.isArray(f.options) ? f.options.map(String) : [];
      const fromDeals = deals.flatMap(d => {
        const cv = d.customFieldValues?.[f.id];
        if (Array.isArray(cv)) return cv.map(String);
        return cv !== undefined && cv !== null && String(cv).trim() !== '' ? [String(cv)] : [];
      });
      return { ...f, _values: [...new Set([...fromOptions, ...fromDeals])] };
    })
    .filter(f => f._values.length > 0 && f._values.length <= 30);
  /* Deal'larda uchraydigan, lekin ro'yxatda yo'q manbalar ham filtrда chiqsin */
  const extraSources = [...new Set(
    deals.map(d => d.source).filter(v => v && !dealSources.some(s => String(s._id) === String(v) || s.name === v))
  )];

  /* Filtrsiz (faqat arxivlanmagan) jami sdelkalar soni — yuqoridagi hisoblagich uchun */
  const totalDealsCount = deals.filter(d => !d.archived).length;

  /* Search + mas'ul + manba + custom maydon filtrlari */
  const q = search.trim().toLowerCase();
  const cfActive = Object.entries(filterCF).filter(([, v]) => v);
  const filteredDeals = deals
    .filter(d => !d.archived)
    .filter(d => !q ||
        d.title.toLowerCase().includes(q) ||
        d.contact?.name?.toLowerCase().includes(q) ||
        d.contact?.phone?.includes(q)
      )
    .filter(d => !filterAssignedTo || ownerIdsOf(d).includes(String(filterAssignedTo)))
    .filter(d => {
      if (!filterSource) return true;
      if (filterSource === '__none__') return !d.source;
      return String(d.source) === filterSource || d.source === filterSource;
    })
    .filter(d => {
      for (const [fid, val] of cfActive) {
        const cv = d.customFieldValues?.[fid];
        if (val === '__none__') {
          if (Array.isArray(cv) ? cv.length : (cv !== undefined && cv !== null && String(cv).trim() !== '')) return false;
          continue;
        }
        if (Array.isArray(cv) ? !cv.map(String).includes(val) : String(cv ?? '') !== val) return false;
      }
      return true;
    })
    .filter(d => {
      if (!filterDateFrom && !filterDateTo) return true;
      const created = new Date(d.createdAt);
      if (filterDateFrom && created < new Date(`${filterDateFrom}T00:00:00`)) return false;
      if (filterDateTo && created > new Date(`${filterDateTo}T23:59:59.999`)) return false;
      return true;
    });

  /* Saralash — standart holatda surish tartibi (order), aks holda tanlangan mezon bo'yicha */
  const sortDeals = (a, b) => {
    switch (sortBy) {
      case 'created_asc':  return new Date(a.createdAt) - new Date(b.createdAt);
      case 'created_desc': return new Date(b.createdAt) - new Date(a.createdAt);
      case 'updated_asc':  return new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt);
      case 'updated_desc': return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      case 'title_asc':    return a.title.localeCompare(b.title, 'uz');
      case 'title_desc':   return b.title.localeCompare(a.title, 'uz');
      default:              return a.order - b.order;
    }
  };

  /* Group deals by stage — faqat faol sdelkalar (yopilganlar "Yopilganlarni ko'rsatish"
     yoqilganda alohida G'olib/Yo'qotilgan ustunlarida ko'rinadi, pastga qarang) */
  const dealsByStage = (funnel?.stages || []).reduce((acc, s) => {
    acc[s._id] = filteredDeals.filter(d => d.status === 'active' && String(d.stageId) === String(s._id)).sort(sortDeals);
    return acc;
  }, {});

  /* Yopilgan sdelkalar — "Yopilganlarni ko'rsatish" yoqilganda, bosqichidan qat'i
     nazar, holatiga (g'olib/yo'qotilgan) qarab ikkita ustunga yig'iladi */
  const closedWonDeals  = filteredDeals.filter(d => d.status === 'won').sort(sortDeals);
  const closedLostDeals = filteredDeals.filter(d => d.status === 'lost').sort(sortDeals);

  const activeDeal = activeId ? deals.find(d => d._id === activeId) : null;

  /* Ommaviy tanlash */
  const toggleSelect = (dealId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId); else next.add(dealId);
      return next;
    });
  };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); setBulkDeleteConfirm(false); };
  // Bosqich bo'yicha "hammasini tanlash" — faqat o'sha bosqichdagi lidlarga tegishli
  const toggleSelectStage = (stageId) => {
    const stageDealIds = (dealsByStage[stageId] || []).map(d => d._id);
    const allIn = stageDealIds.length > 0 && stageDealIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      stageDealIds.forEach(id => allIn ? next.delete(id) : next.add(id));
      return next;
    });
  };

  /* DnD handlers */
  const [overStageId, setOverStageId] = useState(null);   // этап под курсором при перетаскивании (подсвечивается, даже когда курсор над карточкой)
  const handleDragStart = ({ active }) => setActiveId(active.id);
  const handleDragOver = ({ over }) => {
    if (!over) { setOverStageId(null); return; }
    const od = deals.find(d => d._id === over.id);
    setOverStageId(String(od ? od.stageId : over.id));
  };

  // extraValues — majburiy maydonlar oynasida to'ldirilgan qiymatlar (bitimning mavjudlari ustiga qo'yiladi)
  const applyMove = async (deal, targetStageId, value, assignedTo, extraValues) => {
    setDeals(prev => prev.map(d => d._id === deal._id ? { ...d, stageId: targetStageId, value: value ?? d.value } : d));
    try {
      const body = { stageId: targetStageId };
      if (value !== undefined) body.value = value;
      if (assignedTo !== undefined) body.assignedTo = assignedTo;
      if (extraValues) body.customFieldValues = { ...(deal.customFieldValues || {}), ...extraValues };
      const res = await axios.put(`${API}/funnels/${funnelId}/deals/${deal._id}`, body);
      if (res.data?.deal) setDeals(prev => prev.map(d => d._id === deal._id ? res.data.deal : d));
      setReqModal(null);
      // Mas'ul biriktirilgan holatda bosqichda handoffTo sozlangan bo'lishi mumkin -
      // bunda backend bitimni boshqa voronkaga ko'chiradi, taxtani darhol yangilaymiz.
      if (assignedTo !== undefined) load();
    } catch (e) {
      if (isRequirementsError(e)) {
        setDeals(prev => prev.map(d => d._id === deal._id ? deal : d));
        setReqModal({
          deal, missing: requirementsOf(e),
          retry: (vals, val) => applyMove(deal, targetStageId, val ?? value, assignedTo, { ...(extraValues || {}), ...vals }),
        });
        return;
      }
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
      load();
    }
  };

  const submitRequirements = async (vals, val) => {
    if (!reqModal) return;
    setReqBusy(true);
    try { await reqModal.retry(vals, val); } finally { setReqBusy(false); }
  };

  // Перенос сделки в другой этап с теми же правилами (ответственный, причина закрытия, сумма у лида), что и при перетаскивании
  const requestMove = (srcDeal, targetStageId) => {
    const targetStage = funnel?.stages?.find(s => String(s._id) === String(targetStageId));

    // Bosqich "mas'ul tanlash majburiy" deb belgilangan bo'lsa (masalan qabuldan
    // menejerga topshirish) — avval mas'ul so'raladi, tasdiqlangach applyMove chaqiriladi
    // (backend handoffTo bo'lsa bitimni avtomatik boshqa voronkaga ham o'tkazadi).
    if (targetStage?.requireAssigneeOnEnter) {
      setAssignUserId('');
      setPendingAssign({ deal: srcDeal, targetStageId });
      return;
    }

    // "G'olib"/"Yo'qotilgan" deb belgilangan bosqichga tashlansa — sabab so'raladi
    // (menyudagi umumiy yopish bilan bir xil xatti-harakat, drag orqali ham jim yopilmasin)
    if (targetStage?.isWon || targetStage?.isLost) {
      setDragCloseReasonInput('');
      setDragCloseModal({ deal: srcDeal, targetStageId, targetStatus: targetStage.isWon ? 'won' : 'lost' });
      return;
    }

    // Moving from first stage (lead) → any other stage: ask for value
    const firstStageId = funnel?.hasLeadStage !== false ? funnel?.stages?.[0]?._id : null;
    if (firstStageId && String(srcDeal.stageId) === String(firstStageId)) {
      setMoveValue(srcDeal.value ? String(srcDeal.value) : '');
      setPendingMove({ deal: srcDeal, targetStageId });
      return;
    }

    // Normal cross-stage move
    applyMove(srcDeal, targetStageId);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    setOverStageId(null);
    if (!over || active.id === over.id) return;

    const srcDeal = deals.find(d => d._id === active.id);
    if (!srcDeal) return;

    let targetStageId = over.id;
    const overDeal = deals.find(d => d._id === over.id);
    if (overDeal) targetStageId = overDeal.stageId;

    if (String(srcDeal.stageId) === String(targetStageId)) {
      // Same stage reorder
      const stageDeals = deals.filter(d => String(d.stageId) === String(targetStageId));
      const oldIdx = stageDeals.findIndex(d => d._id === srcDeal._id);
      const newIdx = stageDeals.findIndex(d => d._id === overDeal?._id);
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(stageDeals, oldIdx, newIdx).map((d, i) => ({ ...d, order: i }));
      setDeals(prev => prev.map(d => reordered.find(r => r._id === d._id) || d));
      // Har bir deal uchun yangi tartibni serverga saqlaymiz
      reordered.forEach(d => {
        axios.put(`${API}/funnels/${funnelId}/deals/${d._id}`, { order: d.order }).catch(() => load());
      });
      return;
    }

    requestMove(srcDeal, targetStageId);
  };

  const confirmMove = () => {
    if (!pendingMove) return;
    applyMove(pendingMove.deal, pendingMove.targetStageId, Number(moveValue) || 0);
    setPendingMove(null);
    setMoveValue('');
  };

  const cancelMove = () => {
    setPendingMove(null);
    setMoveValue('');
  };

  const confirmAssign = () => {
    if (!pendingAssign || !assignUserId) return;
    if (pendingAssign.bulk) {
      handleBulkStageMove(pendingAssign.targetStageId, assignUserId);
    } else {
      applyMove(pendingAssign.deal, pendingAssign.targetStageId, undefined, assignUserId);
    }
    setPendingAssign(null);
    setAssignUserId('');
  };

  const cancelAssign = () => {
    setPendingAssign(null);
    setAssignUserId('');
  };

  const confirmDragClose = async (extraValues) => {
    if (!dragCloseModal) return;
    const { deal, targetStageId, targetStatus } = dragCloseModal;
    setDragClosing(true);
    try {
      const body = { stageId: targetStageId, status: targetStatus, closeReason: dragCloseReasonInput.trim() };
      if (extraValues) body.customFieldValues = { ...(deal.customFieldValues || {}), ...extraValues };
      const res = await axios.put(`${API}/funnels/${funnelId}/deals/${deal._id}`, body);
      setDeals(prev => prev.map(d => d._id === deal._id ? res.data.deal : d));
      toast.success(targetStatus === 'won' ? t('loc.funnelPage.closedAsWon') : t('loc.funnelPage.closedAsLost'));
      if (targetStatus === 'won') setCelebrate(true);
      setDragCloseModal(null);
      setReqModal(null);
    } catch (e) {
      if (isRequirementsError(e)) {
        setReqModal({ deal, missing: requirementsOf(e), retry: (vals) => confirmDragClose({ ...(extraValues || {}), ...vals }) });
        return;
      }
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setDragClosing(false);
    }
  };

  const cancelDragClose = () => setDragCloseModal(null);

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm(t('funnel.deleteConfirm'))) return;
    setDeals(prev => prev.filter(d => d._id !== dealId));
    try {
      await axios.delete(`${API}/funnels/${funnelId}/deals/${dealId}`);
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
      load();
    }
  };

  // "O'zimga olish" — pool'dagi (mas'ulsiz) lidni o'ziga biriktirib olish
  const handleClaimDeal = async (dealId) => {
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/${dealId}/claim`);
      setDeals(prev => prev.map(d => d._id === dealId ? res.data.deal : d));
      toast.success(t('loc.funnelPage.assignedToYou'));
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
      load();
    }
  };

  // Boshqa varonkaga o'tkazish - ko'rinish cheklovisiz to'liq ro'yxatdan (allFunnelNames),
  // shunda ko'ra olmagan voronkaga ham lid yuborish mumkin bo'ladi.
  const moveTargetFunnels = allFunnelNames.filter(f => String(f._id) !== String(funnelId));
  const moveTargetFunnel  = moveTargetFunnels.find(f => String(f._id) === String(moveFunnelId));
  const moveTargetStage   = moveTargetFunnel?.stages.find(s => String(s._id) === String(moveStageId));

  const openMoveModal = (deal) => {
    setMoveDeal(deal);
    setMoveFunnelId('');
    setMoveStageId('');
    setMoveAssignUserId('');
  };

  const handleMoveFunnel = async (extraValues) => {
    if (!moveDeal || !moveFunnelId || !moveStageId) return;
    if (moveTargetStage?.requireAssigneeOnEnter && !moveAssignUserId) return;
    setMovingFunnel(true);
    try {
      if (extraValues) {
        await axios.put(`${API}/funnels/${funnelId}/deals/${moveDeal._id}`, {
          customFieldValues: { ...(moveDeal.customFieldValues || {}), ...extraValues },
        });
      }
      await axios.post(`${API}/funnels/${funnelId}/deals/${moveDeal._id}/move`, {
        targetFunnelId: moveFunnelId, targetStageId: moveStageId,
        ...(moveAssignUserId ? { assignedTo: moveAssignUserId } : {}),
      });
      setDeals(prev => prev.filter(d => d._id !== moveDeal._id));
      toast.success(t('deals.moveSuccess'));
      setMoveDeal(null);
      setReqModal(null);
    } catch (e) {
      if (isRequirementsError(e)) {
        setReqModal({ deal: moveDeal, missing: requirementsOf(e), retry: (vals) => handleMoveFunnel({ ...(extraValues || {}), ...vals }) });
        return;
      }
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setMovingFunnel(false);
    }
  };

  // Yopilgan sdelkani qayta faollashtirish — istalgan voronka (shu joriysi ham
  // bo'lishi mumkin) va bosqichga qaytariladi
  const reactivateTargetFunnel = allFunnelNames.find(f => String(f._id) === String(reactivateFunnelId));
  const openReactivateModal = (deal) => {
    setReactivateDeal(deal);
    setReactivateFunnelId(String(funnelId));
    setReactivateStageId('');
  };
  const handleReactivate = async () => {
    if (!reactivateDeal || !reactivateFunnelId || !reactivateStageId) return;
    setReactivating(true);
    try {
      if (String(reactivateFunnelId) === String(funnelId)) {
        const res = await axios.put(`${API}/funnels/${funnelId}/deals/${reactivateDeal._id}`, { stageId: reactivateStageId });
        setDeals(prev => prev.map(d => d._id === reactivateDeal._id ? res.data.deal : d));
      } else {
        await axios.post(`${API}/funnels/${funnelId}/deals/${reactivateDeal._id}/move`, {
          targetFunnelId: reactivateFunnelId, targetStageId: reactivateStageId,
        });
        setDeals(prev => prev.filter(d => d._id !== reactivateDeal._id));
      }
      toast.success(t('loc.funnelPage.reactivated'));
      setReactivateDeal(null);
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setReactivating(false);
    }
  };

  // Ommaviy: tanlangan lidlarni shu varonka ichida boshqa bosqichga o'tkazish
  const handleBulkStageMove = async (stageId, assignedTo) => {
    if (!stageId || selectedIds.size === 0) return;
    setBulkMovingStage(true);
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/bulk-move`, {
        dealIds: [...selectedIds], targetStageId: stageId,
        ...(assignedTo ? { assignedTo } : {}),
      });
      const moved = res.data.moved || [];
      setDeals(prev => prev.map(d => moved.find(m => m._id === d._id) || d));
      toast.success(t('funnel.bulkMoved', { n: moved.length }) + (res.data.skipped ? t('funnel.bulkSkipped', { n: res.data.skipped }) : ''));
      if (res.data.blocked) toast.error(t('stageRules.bulkBlocked', { n: res.data.blocked }), { duration: 6000 });
      exitSelectMode();
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setBulkMovingStage(false);
    }
  };

  // Ommaviy: tanlangan lidlarni boshqa varonkaga o'tkazish
  const bulkMoveTargetFunnel = moveTargetFunnels.find(f => String(f._id) === String(bulkMoveFunnelId));
  const openBulkMoveModal = () => {
    setBulkMoveFunnelId(''); setBulkMoveStageId(''); setBulkMoveOpen(true);
  };
  const handleBulkMoveFunnel = async () => {
    if (!bulkMoveFunnelId || !bulkMoveStageId || selectedIds.size === 0) return;
    setBulkMovingFunnel(true);
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/bulk-move`, {
        dealIds: [...selectedIds], targetFunnelId: bulkMoveFunnelId, targetStageId: bulkMoveStageId,
      });
      const moved = res.data.moved || [];
      const movedIds = new Set(moved.map(d => d._id));
      setDeals(prev => prev.filter(d => !movedIds.has(d._id)));
      toast.success(t('funnel.bulkTransferred', { n: moved.length }) + (res.data.skipped ? t('funnel.bulkSkipped', { n: res.data.skipped }) : ''));
      if (res.data.blocked) toast.error(t('stageRules.bulkBlocked', { n: res.data.blocked }), { duration: 6000 });
      setBulkMoveOpen(false);
      exitSelectMode();
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setBulkMovingFunnel(false);
    }
  };

  // Ommaviy: tanlangan lidlarni o'chirish
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.delete(`${API}/funnels/${funnelId}/deals`, { data: { dealIds: [...selectedIds] } });
      const deletedIds = selectedIds;
      setDeals(prev => prev.filter(d => !deletedIds.has(d._id)));
      toast.success(t('funnel.bulkDeleted', { n: res.data.deleted }) + (res.data.skipped ? t('funnel.bulkSkipped', { n: res.data.skipped }) : ''));
      exitSelectMode();
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Arxivlash — bitta kartochkadan ("Tanlash" rejimisiz) yoki tanlangan bir nechtasidan,
  // ikkalasida ham sabab (izoh) so'raladi
  const openBulkArchiveModal = () => {
    if (selectedIds.size === 0) return;
    setBulkArchiveReason('');
    setArchiveTargetIds([...selectedIds]);
  };
  const openSingleArchiveModal = (deal) => {
    setBulkArchiveReason('');
    setArchiveTargetIds([deal._id]);
  };
  const handleBulkArchive = async () => {
    if (!archiveTargetIds?.length || !bulkArchiveReason.trim()) return;
    setBulkArchiving(true);
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/bulk-archive`, {
        dealIds: archiveTargetIds, archived: true, reason: bulkArchiveReason.trim(),
      });
      const archivedIds = new Set(archiveTargetIds);
      setDeals(prev => prev.filter(d => !archivedIds.has(d._id)));
      toast.success(t('funnel.bulkArchived', { n: res.data.updated }) + (res.data.skipped ? t('funnel.bulkSkipped', { n: res.data.skipped }) : ''));
      setArchiveTargetIds(null);
      exitSelectMode();
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setBulkArchiving(false);
    }
  };

  // Ommaviy: tanlangan lidlarni kimgadir biriktirish
  const handleBulkAssign = async (userId) => {
    if (selectedIds.size === 0) return;
    setBulkAssigning(true);
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/bulk-assign`, {
        dealIds: [...selectedIds], assignedTo: userId || null,
      });
      const assignee = users.find(u => u._id === userId);
      setDeals(prev => prev.map(d => selectedIds.has(d._id) ? { ...d, assignedTo: assignee || null, assignees: assignee ? [assignee] : [] } : d));
      toast.success(t('funnel.bulkAssigned', { n: res.data.updated }) + (res.data.skipped ? t('funnel.bulkSkipped', { n: res.data.skipped }) : ''));
      exitSelectMode();
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.funnelPage.error'));
    } finally {
      setBulkAssigning(false);
    }
  };

  // F-13: quick create deal from column header
  const handleQuickCreate = async ({ title, stageId, value, notes, assignees, contact }) => {
    const res = await axios.post(`${API}/funnels/${funnelId}/deals`, {
      title, stageId, value: Number(value) || 0, notes,
      assignees: assignees || [], contact: contact || null,
    });
    setDeals(prev => prev.some(d => d._id === res.data.deal._id) ? prev : [...prev, res.data.deal]);
    toast.success(t('loc.funnelPage.leadAdded'));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 animate-spin text-ink-tertiary" />
    </div>
  );

  if (!funnel) return (
    <div className="flex items-center justify-center h-full text-ink-tertiary text-sm">
      {t('funnel.noFunnel')}
    </div>
  );

  // Шапка: чипы активных фильтров + сброс (поиск и «закрытые» живут вне поповера).
  const srcLabel = (v) => v === '__none__' ? t('funnel.noSource') : (dealSources.find(s => String(s._id) === String(v))?.name || v);
  const filterChips = [
    filterAssignedTo && { key: 'assignee', label: users.find(u => String(u._id) === String(filterAssignedTo))?.name || '—', onRemove: () => setFilterAssignedTo('') },
    filterSource && { key: 'source', label: srcLabel(filterSource), onRemove: () => setFilterSource('') },
    (filterDateFrom || filterDateTo) && { key: 'dates', label: t('funnel.chipDates', { from: filterDateFrom || '…', to: filterDateTo || '…' }), onRemove: () => { setFilterDateFrom(''); setFilterDateTo(''); } },
    ...Object.entries(filterCF).filter(([, v]) => v).map(([fid, v]) => ({
      key: 'cf' + fid,
      label: `${filterableFields.find(f => f.id === fid)?.key || ''}: ${v === '__none__' ? '—' : v}`,
      onRemove: () => setFilterCF(prev => ({ ...prev, [fid]: '' })),
    })),
  ].filter(Boolean);
  const popoverFilterCount = filterChips.length + (sortBy ? 1 : 0);
  const resetPopoverFilters = () => { setFilterAssignedTo(''); setFilterSource(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterCF({}); setSortBy(''); };
  const clearAllFilters = () => { resetPopoverFilters(); setSearch(''); };

  return (
    <div className="flex flex-col h-full">
      {/* Header: заголовок · поиск · фильтры · вид · «Ещё» · главное действие */}
      <PageHeader
        title={funnel.name}
        count={filteredDeals.length !== totalDealsCount
          ? t('funnel.dealsCountFiltered', { shown: filteredDeals.length, total: totalDealsCount })
          : t('funnel.dealsCount', { n: totalDealsCount })}
        below={<FilterChips chips={filterChips} onClear={clearAllFilters} />}
      >
        <SearchInput className="w-full sm:w-56 md:w-64 order-last sm:order-none" value={search} onChange={setSearch} placeholder={t('funnel.dealSearch')} />

        <FilterPopover activeCount={popoverFilterCount} onReset={resetPopoverFilters}>
          <FilterField label={t('ui.sort')}>
            <FilterSelect value={sortBy} onChange={setSortBy}>
              <option value="">{t('funnel.sortManual')}</option>
              <option value="created_asc">{t('funnel.sortCreatedAsc')}</option>
              <option value="created_desc">{t('funnel.sortCreatedDesc')}</option>
              <option value="updated_asc">{t('funnel.sortUpdatedAsc')}</option>
              <option value="updated_desc">{t('funnel.sortUpdatedDesc')}</option>
              <option value="title_asc">{t('funnel.sortTitleAsc')}</option>
              <option value="title_desc">{t('funnel.sortTitleDesc')}</option>
            </FilterSelect>
          </FilterField>

          <FilterField label={t('ui.assignee')}>
            <FilterSelect value={filterAssignedTo} onChange={setFilterAssignedTo}>
              <option value="">{t('tasks.allAssignees')}</option>
              {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
            </FilterSelect>
          </FilterField>

          {(dealSources.length > 0 || extraSources.length > 0) && (
            <FilterField label={t('ui.source')}>
              <FilterSelect value={filterSource} onChange={setFilterSource}>
                <option value="">{t('funnel.allSources')}</option>
                {dealSources.map(s => <option key={String(s._id)} value={String(s._id)}>{s.name}</option>)}
                {extraSources.map(v => <option key={v} value={v}>{v}</option>)}
                <option value="__none__">{t('funnel.noSource')}</option>
              </FilterSelect>
            </FilterField>
          )}

          {filterableFields.map(f => (
            <FilterField key={f.id} label={f.key}>
              <FilterSelect value={filterCF[f.id] || ''} onChange={v => setFilterCF(prev => ({ ...prev, [f.id]: v }))}>
                <option value="">{t('funnel.cfAll', { name: f.key })}</option>
                {f._values.map(o => <option key={o} value={o}>{o}</option>)}
                <option value="__none__">{t('funnel.cfNone', { name: f.key })}</option>
              </FilterSelect>
            </FilterField>
          ))}

          <FilterField label={t('ui.created')}>
            <div className="flex items-center gap-2">
              <input type="date" title={t('funnel.dateFrom')} className="input !py-1.5 text-sm" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
              <span className="text-ink-tertiary">—</span>
              <input type="date" title={t('funnel.dateTo')} className="input !py-1.5 text-sm" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
            </div>
          </FilterField>
        </FilterPopover>

        <Segmented
          value={showClosed ? 'closed' : 'active'}
          onChange={v => setShowClosed(v === 'closed')}
          options={[
            { value: 'active', label: t('funnel.viewActive') },
            { value: 'closed', label: t('funnel.viewClosed'), icon: Trophy },
          ]}
        />

        <MoreMenu items={[
          { icon: Check,    label: selectMode ? t('funnel.selectCancel') : t('funnel.select'), onClick: () => (selectMode ? exitSelectMode() : setSelectMode(true)), active: selectMode, hidden: !canEdit },
          { icon: Archive,  label: t('funnel.archiveBtn'), onClick: () => setShowArchive(true), hidden: !canEdit },
          { icon: Download, label: t('funnel.export'), onClick: handleExport, disabled: exporting, divider: true },
          { icon: Upload,   label: t('funnel.import'), onClick: () => setShowImport(true), hidden: !canCreate },
        ]} />

        {funnel.stages.length >= 1 && canCreate && (
          <button onClick={() => navigate(`/funnel/${funnelId}/deal/new`)} className="btn-primary btn-md flex items-center gap-2 shrink-0">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">{t('funnel.newLead')}</span>
          </button>
        )}
      </PageHeader>

      {/* Ommaviy amallar paneli */}
      {selectMode && (
        <div className="px-4 md:px-6 py-2 border-b border-primary-100 bg-primary-50 flex flex-wrap items-center gap-3 shrink-0">
          <span className="text-sm font-semibold text-primary-700">{t('loc.funnelPage.selected', { size: selectedIds.size })}</span>
          <div className="flex items-center gap-2 ml-auto">
            <div className="relative shrink-0">
              <select
                className="pl-3 pr-8 py-2 text-sm bg-white border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-300 appearance-none disabled:opacity-60"
                value=""
                disabled={selectedIds.size === 0 || bulkMovingStage}
                onChange={e => {
                  const stageId = e.target.value;
                  if (!stageId) return;
                  const stage = funnel.stages.find(s => String(s._id) === String(stageId));
                  if (stage?.requireAssigneeOnEnter) {
                    setAssignUserId('');
                    setPendingAssign({ bulk: true, targetStageId: stageId });
                  } else {
                    handleBulkStageMove(stageId);
                  }
                }}
              >
                <option value="">{t('loc.funnelPage.moveToStage')}</option>
                {funnel.stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-disabled pointer-events-none" />
            </div>
            <button
              onClick={openBulkMoveModal}
              disabled={selectedIds.size === 0}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-surface-200 text-ink-secondary hover:border-surface-300 hover:text-ink transition-colors disabled:opacity-60"
            >
              <Layers className="w-4 h-4" /> {' '}{t('loc.funnelPage.toAnotherFunnel')}
            </button>
            <div className="relative shrink-0">
              <select
                className="pl-8 pr-8 py-2 text-sm bg-white border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-300 appearance-none disabled:opacity-60"
                value=""
                disabled={selectedIds.size === 0 || bulkAssigning}
                onChange={e => { if (e.target.value === '__unassign__') handleBulkAssign(null); else if (e.target.value) handleBulkAssign(e.target.value); }}
              >
                <option value="">{t('loc.funnelPage.assign')}</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                <option value="__unassign__">{t('loc.funnelPage.unassign')}</option>
              </select>
              <UserCheck className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-disabled pointer-events-none" />
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-disabled pointer-events-none" />
            </div>
            <button
              onClick={openBulkArchiveModal}
              disabled={selectedIds.size === 0}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-surface-200 text-ink-secondary hover:border-surface-300 hover:text-ink transition-colors disabled:opacity-60"
            >
              <Archive className="w-4 h-4" /> {' '}{t('loc.funnelPage.archive')}
            </button>
            {canDelete && (
              bulkDeleteConfirm ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-ink">{t('loc.funnelPage.deletePermanently')}</span>
                  <button onClick={() => setBulkDeleteConfirm(false)} disabled={bulkDeleting}
                    className="px-2.5 py-2 rounded-xl text-sm font-medium border border-surface-200 text-ink-secondary hover:bg-surface-50 transition-colors">
                   {t('loc.funnelPage.no')}
                  </button>
                  <button onClick={handleBulkDelete} disabled={bulkDeleting}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors">
                    {bulkDeleting && <Loader2 className="w-4 h-4 animate-spin" />} {' '}{t('loc.funnelPage.yesDelete')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setBulkDeleteConfirm(true)}
                  disabled={selectedIds.size === 0}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  <Trash2 className="w-4 h-4" /> {' '}{t('loc.funnelPage.delete')}
                </button>
              )
            )}
            <button onClick={exitSelectMode} className="p-2 rounded-lg text-ink-tertiary hover:bg-surface-100" title={t('loc.funnelPage.close')}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Kanban board */}
      {funnel.stages.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-ink-tertiary text-sm flex-col gap-2">
          <p>{t('loc.funnelPage.thisFunnelHasNoStages')}</p>
          <p className="text-xs">{t('loc.funnelPage.addStagesInSettingsFunnels')}</p>
        </div>
      ) : showClosed ? (
        <div ref={boardRef} className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-4 h-full px-3 sm:px-6 py-5 items-stretch justify-center">
            <ClosedColumn
              label={funnel.stages.find(s => s.isWon)?.name || t('loc.funnelPage.won')}
              color={funnel.stages.find(s => s.isWon)?.color || '#10b981'}
              icon={Trophy} deals={closedWonDeals} currency={currency}
              onOpen={(deal) => navigate(`/funnel/${funnelId}/deal/${deal._id}`)}
              onReactivate={openReactivateModal} canEdit={canEdit} />
            <ClosedColumn
              label={funnel.stages.find(s => s.isLost)?.name || t('loc.funnelPage.lost')}
              color={funnel.stages.find(s => s.isLost)?.color || '#ef4444'}
              icon={XCircle} deals={closedLostDeals} currency={currency}
              onOpen={(deal) => navigate(`/funnel/${funnelId}/deal/${deal._id}`)}
              onReactivate={openReactivateModal} canEdit={canEdit} />
          </div>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={() => { setActiveId(null); setOverStageId(null); }}>
          <StageChips stages={funnel.stages} dealsByStage={dealsByStage} boardRef={boardRef} />
          <div ref={boardRef} className="flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory sm:snap-none scroll-px-3">
            <div className={`flex gap-3 sm:gap-4 h-full px-3 sm:px-6 py-3 sm:py-5 items-stretch ${boardFits ? 'justify-center' : ''}`}>
              {funnel.stages.map((stage, idx) => (
                <StageColumn
                  key={stage._id}
                  stage={stage}
                  deals={dealsByStage[stage._id] || []}
                  currency={currency}
                  isFirst={idx === 0 && funnel.hasLeadStage !== false}
                  onOpen={(dealId) => navigate(`/funnel/${funnelId}/deal/${dealId}`)}
                  onDelete={handleDeleteDeal}
                  onMove={openMoveModal}
                  onArchive={openSingleArchiveModal}
                  onClaim={handleClaimDeal}
                  onMoveStage={setStageSheetDeal}
                  highlight={activeId != null && overStageId === String(stage._id)}
                  onQuickAdd={setQuickStageId}
                  canCreate={canCreate} canEdit={canEdit} canDelete={canDelete}
                  selectMode={selectMode} selectedIds={selectedIds} onToggleSelect={toggleSelect} onSelectAllStage={toggleSelectStage}
                />
              ))}
            </div>
          </div>
          <DragOverlay dropAnimation={{ duration: 220, easing: 'cubic-bezier(.2,.8,.2,1)' }}>
            {activeDeal && (
              <DealCard
                deal={activeDeal}
                isLead={funnel.hasLeadStage !== false && funnel.stages[0] && String(activeDeal.stageId) === String(funnel.stages[0]._id)}
                accent={funnel.stages.find(st => String(st._id) === String(activeDeal.stageId))?.color}
                currency={currency}
                onEdit={() => {}} onDelete={() => {}} onMove={() => {}} overlay
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {celebrate && <Celebrate onDone={() => setCelebrate(false)} />}

      {/* Телефон: выбор этапа для сделки (перетаскивание на сенсорном экране неудобно) */}
      {stageSheetDeal && (
        <Modal title={t('deals.moveStageTitle')} onClose={() => setStageSheetDeal(null)} size="sm" closeLabel={t('loc.funnelPage.close')}>
          <p className="text-sm text-ink-secondary mb-3 truncate">{srv(stageSheetDeal.title)}</p>
          <div className="space-y-1.5">
            {funnel.stages.map(st => {
              const current = String(st._id) === String(stageSheetDeal.stageId);
              return (
                <button key={st._id} type="button" disabled={current}
                  onClick={() => { const d = stageSheetDeal; setStageSheetDeal(null); requestMove(d, st._id); }}
                  className={`w-full flex items-center gap-3 px-3 min-h-[48px] rounded-xl text-left text-sm font-medium border transition-colors ${current ? 'bg-primary-500/10 border-primary-500/40 text-primary-700' : 'bg-card border-surface-200 text-ink hover:bg-surface-100'}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: st.color }} />
                  <span className="flex-1 truncate">{st.name}</span>
                  {current && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        </Modal>
      )}

      {/* Excel import modal */}
      {showImport && (
        <ImportLeadsModal
          funnelId={funnelId}
          funnelName={funnel.name}
          onClose={() => setShowImport(false)}
          onDone={load}
        />
      )}

      {/* F-13: Quick-add deal modal */}
      {quickStageId && funnel && (
        <DealModal
          stageId={quickStageId}
          stages={funnel.stages}
          contacts={contacts}
          users={users}
          deal={null}
          isLead={funnel.hasLeadStage !== false && funnel.stages[0] && String(quickStageId) === String(funnel.stages[0]._id)}
          currency={currency}
          onContactCreated={(c) => { setContacts(cur => cur.some(x => x._id === c._id) ? cur : [c, ...cur]); dispatch(invalidateContacts()); }}
          onSave={handleQuickCreate}
          onClose={() => setQuickStageId(null)}
        />
      )}

      {/* Value modal — shown when dragging lead → sdelka */}
      {pendingMove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={cancelMove} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-ink mb-1">{t('funnel.moveTitle')}</h3>
            <p className="text-sm text-ink-tertiary mb-5">
              <span className="font-medium text-ink">{pendingMove.deal.title}</span>
            </p>
            <div className="relative mb-5">
              <input
                autoFocus
                type="number"
                min="0"
                className="input w-full pr-14 text-lg font-semibold"
                placeholder="0"
                value={moveValue}
                onChange={e => setMoveValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmMove(); if (e.key === 'Escape') cancelMove(); }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-tertiary font-medium pointer-events-none">
                {currency}
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={cancelMove} className="btn-secondary btn-md flex-1">{t('funnel.moveCancel')}</button>
              <button onClick={confirmMove} className="btn-primary btn-md flex-1">{t('funnel.moveSave')}</button>
            </div>
          </div>
        </div>
      )}

      <RequirementsModal open={!!reqModal} missing={reqModal?.missing} fields={flattenFields(cfSections)}
        values={reqModal?.deal?.customFieldValues} value={reqModal?.deal?.value} busy={reqBusy}
        onSubmit={submitRequirements} onClose={() => setReqModal(null)} />

      {/* Mas'ul tanlash modali — bosqich "requireAssigneeOnEnter" bo'lganda, sudrab tashlaganda */}
      {pendingAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={cancelAssign} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-ink mb-1">{t('loc.funnelPage.chooseAnAssignee')}</h3>
            <p className="text-sm text-ink-tertiary mb-5">
              {pendingAssign.bulk
                ? <span className="font-medium text-ink">{t('loc.funnelPage.dealsSelected', { size: selectedIds.size })}</span>
                : <span className="font-medium text-ink">{pendingAssign.deal.title}</span>}
            </p>
            <select
              autoFocus
              className="input w-full mb-5"
              value={assignUserId}
              onChange={e => setAssignUserId(e.target.value)}
            >
              <option value="">{t('loc.funnelPage.chooseAnEmployee')}</option>
              {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>
            <div className="flex gap-2">
              <button onClick={cancelAssign} className="btn-secondary btn-md flex-1">{t('loc.funnelPage.cancel')}</button>
              <button onClick={confirmAssign} disabled={!assignUserId} className="btn-primary btn-md flex-1 disabled:opacity-50">{t('loc.funnelPage.confirm')}</button>
            </div>
          </div>
        </div>
      )}

      {/* "G'olib"/"Yo'qotilgan" bosqichiga sudrab tashlanganda — sabab so'raladi */}
      {dragCloseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className={`text-base font-semibold mb-1 flex items-center gap-2 ${dragCloseModal.targetStatus === 'won' ? 'text-emerald-700' : 'text-red-600'}`}>
              {dragCloseModal.targetStatus === 'won' ? <Trophy className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {dragCloseModal.targetStatus === 'won' ? t('loc.funnelPage.closeAsWon') : t('loc.funnelPage.closeAsLost')}
            </p>
            <p className="text-sm text-ink-tertiary mb-4">
              <span className="font-medium text-ink">{dragCloseModal.deal.title}</span>
            </p>
            <label className="block text-xs font-medium text-ink-secondary mb-1">
             {t('loc.funnelPage.reasonOptional')}{dragCloseModal.targetStatus === 'lost' && t('loc.funnelPage.whyWasItLost')}
            </label>
            {(dragCloseModal.targetStatus === 'won' ? closeReasons.won : closeReasons.lost).length > 0 ? (
              <select
                autoFocus
                className="input w-full mb-5"
                value={dragCloseReasonInput}
                onChange={e => setDragCloseReasonInput(e.target.value)}
              >
                <option value="">{t('loc.funnelPage.noReasonSelected')}</option>
                {(dragCloseModal.targetStatus === 'won' ? closeReasons.won : closeReasons.lost).map(r => (
                  <option key={r._id} value={r.name}>{r.name}</option>
                ))}
              </select>
            ) : (
              <textarea
                autoFocus
                className="input w-full mb-5 resize-none"
                rows={3}
                value={dragCloseReasonInput}
                onChange={e => setDragCloseReasonInput(e.target.value)}
                placeholder={dragCloseModal.targetStatus === 'won' ? t('loc.funnelPage.eGTheClientSigned') : t('loc.funnelPage.eGThePriceDid')}
              />
            )}
            <div className="flex gap-2">
              <button onClick={cancelDragClose} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={() => confirmDragClose()}
                disabled={dragClosing}
                className={`btn-md flex-1 flex items-center justify-center gap-2 text-white rounded-xl font-medium transition-colors ${
                  dragCloseModal.targetStatus === 'won' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {dragClosing && <Loader2 className="w-4 h-4 animate-spin" />}
               {t('loc.funnelPage.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boshqa varonkaga o'tkazish modal */}
      {moveDeal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className="text-base font-semibold text-ink mb-1">{t('deals.moveToFunnel')}</p>
            <p className="text-sm text-ink-tertiary mb-4">
              <span className="font-medium text-ink">{moveDeal.title}</span>
            </p>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.moveFunnelLabel')}</label>
            <select
              className="input w-full mb-3"
              value={moveFunnelId}
              onChange={e => { setMoveFunnelId(e.target.value); setMoveStageId(''); }}
            >
              <option value="">{t('deals.moveFunnelPlaceholder')}</option>
              {moveTargetFunnels.map(f => <option key={f._id} value={f._id}>{f.name}</option>)}
            </select>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.stage')}</label>
            <select
              className="input w-full mb-5"
              value={moveStageId}
              onChange={e => setMoveStageId(e.target.value)}
              disabled={!moveTargetFunnel}
            >
              <option value="">{t('deals.moveStagePlaceholder')}</option>
              {(moveTargetFunnel?.stages || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>

            {moveTargetStage?.requireAssigneeOnEnter && (
              <>
                <label className="block text-xs font-medium text-ink-secondary mb-1">{t('loc.funnelPage.responsibleEmployee')}</label>
                <select
                  className="input w-full mb-5"
                  value={moveAssignUserId}
                  onChange={e => setMoveAssignUserId(e.target.value)}
                >
                  <option value="">{t('loc.funnelPage.chooseAnEmployee')}</option>
                  {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                </select>
              </>
            )}

            <div className="flex gap-2">
              <button onClick={() => setMoveDeal(null)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={() => handleMoveFunnel()}
                disabled={!moveFunnelId || !moveStageId || movingFunnel || (moveTargetStage?.requireAssigneeOnEnter && !moveAssignUserId)}
                className="btn-md btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {movingFunnel && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('deals.moveSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yopilgan sdelkani qayta faollashtirish modal */}
      {reactivateDeal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className="text-base font-semibold text-ink mb-1 flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-primary-600" /> {' '}{t('loc.funnelPage.reactivate')}
            </p>
            <p className="text-sm text-ink-tertiary mb-4">
              <span className="font-medium text-ink">{reactivateDeal.title}</span>
            </p>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.moveFunnelLabel')}</label>
            <select
              className="input w-full mb-3"
              value={reactivateFunnelId}
              onChange={e => { setReactivateFunnelId(e.target.value); setReactivateStageId(''); }}
            >
              {allFunnelNames.map(f => <option key={f._id} value={f._id}>{f.name}{String(f._id) === String(funnelId) ? ` (${funnel.name})` : ''}</option>)}
            </select>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.stage')}</label>
            <select
              className="input w-full mb-5"
              value={reactivateStageId}
              onChange={e => setReactivateStageId(e.target.value)}
              disabled={!reactivateTargetFunnel}
            >
              <option value="">{t('deals.moveStagePlaceholder')}</option>
              {(reactivateTargetFunnel?.stages || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>

            <div className="flex gap-2">
              <button onClick={() => setReactivateDeal(null)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={handleReactivate}
                disabled={!reactivateFunnelId || !reactivateStageId || reactivating}
                className="btn-md btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {reactivating && <Loader2 className="w-4 h-4 animate-spin" />}
               {t('loc.funnelPage.restore')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ommaviy: boshqa varonkaga o'tkazish modal */}
      {bulkMoveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className="text-base font-semibold text-ink mb-1">{t('loc.funnelPage.moveToAnotherFunnel')}</p>
            <p className="text-sm text-ink-tertiary mb-4">
              <span className="font-medium text-ink">{t('loc.funnelPage.leads', { size: selectedIds.size })}</span> {' '}{t('loc.funnelPage.selected2')}
            </p>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.moveFunnelLabel')}</label>
            <select
              className="input w-full mb-3"
              value={bulkMoveFunnelId}
              onChange={e => { setBulkMoveFunnelId(e.target.value); setBulkMoveStageId(''); }}
            >
              <option value="">{t('deals.moveFunnelPlaceholder')}</option>
              {moveTargetFunnels.map(f => <option key={f._id} value={f._id}>{f.name}</option>)}
            </select>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.stage')}</label>
            <select
              className="input w-full mb-5"
              value={bulkMoveStageId}
              onChange={e => setBulkMoveStageId(e.target.value)}
              disabled={!bulkMoveTargetFunnel}
            >
              <option value="">{t('deals.moveStagePlaceholder')}</option>
              {(bulkMoveTargetFunnel?.stages || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>

            <div className="flex gap-2">
              <button onClick={() => setBulkMoveOpen(false)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={handleBulkMoveFunnel}
                disabled={!bulkMoveFunnelId || !bulkMoveStageId || bulkMovingFunnel}
                className="btn-md btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {bulkMovingFunnel && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('deals.moveSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Arxivlash sababi modal — bitta yoki bir nechta lid */}
      {archiveTargetIds && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className="text-base font-semibold text-ink mb-1 flex items-center gap-2">
              <Archive className="w-4 h-4" /> {' '}{t('loc.funnelPage.archive')}
            </p>
            <p className="text-sm text-ink-tertiary mb-4">
              <span className="font-medium text-ink">{t('loc.funnelPage.leads2', { length: archiveTargetIds.length })}</span> {' '}{t('loc.funnelPage.willBeArchived')}
            </p>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('loc.funnelPage.reasonComment')}</label>
            <textarea
              autoFocus
              className="input w-full mb-5 resize-none"
              rows={3}
              value={bulkArchiveReason}
              onChange={e => setBulkArchiveReason(e.target.value)}
              placeholder={t('loc.funnelPage.whyIsItBeingArchived')}
            />

            <div className="flex gap-2">
              <button onClick={() => setArchiveTargetIds(null)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={handleBulkArchive}
                disabled={!bulkArchiveReason.trim() || bulkArchiving}
                className="btn-md btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {bulkArchiving && <Loader2 className="w-4 h-4 animate-spin" />}
               {t('loc.funnelPage.archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Arxivlangan lidlar */}
      {showArchive && (
        <DealArchiveModal
          funnelId={funnelId}
          stages={funnel.stages}
          onClose={() => setShowArchive(false)}
          onRestored={load}
          canEdit={canEdit} canDelete={canDelete}
        />
      )}

    </div>
  );
}
